import type { BlockSelection, Command, TextSelection } from '@blocksuite/std';

import { getViewportElement } from '../../utils/index.js';

export interface SelectionRect {
  width: number;
  height: number;
  top: number;
  left: number;

  /**
   * The block id that the rect is in. Only available for block selections.
   */
  blockId?: string;
}

export const getSelectionRectsCommand: Command<
  {
    currentTextSelection?: TextSelection;
    currentBlockSelections?: BlockSelection[];
    textSelection?: TextSelection;
    blockSelections?: BlockSelection[];
    isTextBound?: boolean;
  },
  {
    selectionRects: SelectionRect[];
  }
> = (ctx, next) => {
  let textSelection;
  let blockSelections;
  let isTextBound = ctx.isTextBound || false;

  // priority parameters
  if (ctx.textSelection) {
    textSelection = ctx.textSelection;
  } else if (ctx.blockSelections) {
    blockSelections = ctx.blockSelections;
  } else if (ctx.currentTextSelection) {
    textSelection = ctx.currentTextSelection;
  } else if (ctx.currentBlockSelections) {
    blockSelections = ctx.currentBlockSelections;
  } else {
    console.error(
      'No selection provided, may forgot to call getTextSelection or getBlockSelections or provide the selection directly.'
    );
    return;
  }

  const { std } = ctx;

  const container = getViewportElement(std.host);
  const containerRect = container?.getBoundingClientRect();

  if (textSelection) {
    const range = std.range.textSelectionToRange(textSelection);

    if (range) {
      return next({
        selectionRects: getRangeRects(range, container, isTextBound),
      });
    }
  } else if (blockSelections && blockSelections.length > 0) {
    const result = blockSelections
      .map(blockSelection => {
        const block = std.view.getBlock(blockSelection.blockId);
        if (!block) return null;

        const rect = block.getBoundingClientRect();

        return {
          width: rect.width,
          height: rect.height,
          top:
            rect.top - (containerRect?.top ?? 0) + (container?.scrollTop ?? 0),
          left:
            rect.left -
            (containerRect?.left ?? 0) +
            (container?.scrollLeft ?? 0),
          blockId: blockSelection.blockId,
        };
      })
      .filter(rect => !!rect);

    return next({ selectionRects: result });
  }

  return;
};

function covers(rect1: SelectionRect, rect2: SelectionRect): boolean {
  return (
    rect1.left <= rect2.left &&
    rect1.top <= rect2.top &&
    rect1.left + rect1.width >= rect2.left + rect2.width &&
    rect1.top + rect1.height >= rect2.top + rect2.height
  );
}

function intersects(rect1: SelectionRect, rect2: SelectionRect): boolean {
  return (
    rect1.left <= rect2.left + rect2.width &&
    rect1.left + rect1.width >= rect2.left &&
    rect1.top <= rect2.top + rect2.height &&
    rect1.top + rect1.height >= rect2.top
  );
}

function merge(rect1: SelectionRect, rect2: SelectionRect): SelectionRect {
  const left = Math.min(rect1.left, rect2.left);
  const top = Math.min(rect1.top, rect2.top);
  const right = Math.max(rect1.left + rect1.width, rect2.left + rect2.width);
  const bottom = Math.max(rect1.top + rect1.height, rect2.top + rect2.height);

  return {
    width: right - left,
    height: bottom - top,
    top,
    left,
  };
}

export function filterCoveringRects(rects: SelectionRect[]): SelectionRect[] {
  let mergedRects: SelectionRect[] = [];
  let hasChanges: boolean;

  do {
    hasChanges = false;
    const newMergedRects: SelectionRect[] = [...mergedRects];

    for (const rect of rects) {
      let merged = false;

      for (let i = 0; i < newMergedRects.length; i++) {
        if (covers(newMergedRects[i], rect)) {
          merged = true;
          break;
        } else if (intersects(newMergedRects[i], rect)) {
          newMergedRects[i] = merge(newMergedRects[i], rect);
          merged = true;
          hasChanges = true;
          break;
        }
      }

      if (!merged) {
        newMergedRects.push(rect);
      }
    }

    if (!hasChanges) {
      for (let i = 0; i < newMergedRects.length; i++) {
        for (let j = i + 1; j < newMergedRects.length; j++) {
          if (intersects(newMergedRects[i], newMergedRects[j])) {
            newMergedRects[i] = merge(newMergedRects[i], newMergedRects[j]);
            newMergedRects.splice(j, 1);
            hasChanges = true;
            break;
          }
        }
      }
    }

    mergedRects = newMergedRects;
  } while (hasChanges);

  return mergedRects;
}

/**
 * Get the actual text bounds for a line at a specific Y position
 * This helps us avoid full-width rectangles for middle lines in multi-line selections
 */
function getTextBoundAtLine(
  range: Range,
  lineTop: number,
  lineHeight: number
): { left: number; right: number } | null {
  const startContainer = range.startContainer;
  const endContainer = range.endContainer;
  
  // Get all text nodes in the range
  const walker = document.createTreeWalker(
    range.commonAncestorContainer,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        if (!range.intersectsNode(node)) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );
  
  let minLeft = Infinity;
  let maxRight = -Infinity;
  let node: Node | null;
  
  while ((node = walker.nextNode())) {
    if (node.nodeType !== Node.TEXT_NODE || !node.textContent?.trim()) {
      continue;
    }
    
    const textNode = node as Text;
    const textRange = document.createRange();
    textRange.selectNodeContents(textNode);
    
    const rects = Array.from(textRange.getClientRects());
    
    // Find rects that match this line (within the Y position range)
    for (const rect of rects) {
      // Check if this rect is on the target line (with some tolerance)
      if (Math.abs(rect.top - lineTop) < lineHeight / 2) {
        // Determine the actual start/end offsets for this text node
        let startOffset = 0;
        let endOffset = textNode.textContent?.length ?? 0;
        
        if (textNode === startContainer && textNode === endContainer) {
          startOffset = range.startOffset;
          endOffset = range.endOffset;
        } else if (textNode === startContainer) {
          startOffset = range.startOffset;
        } else if (textNode === endContainer) {
          endOffset = range.endOffset;
        }
        
        // Create a range for just the selected portion
        const partialRange = document.createRange();
        partialRange.setStart(textNode, startOffset);
        partialRange.setEnd(textNode, endOffset);
        
        const partialRects = Array.from(partialRange.getClientRects());
        for (const partialRect of partialRects) {
          if (Math.abs(partialRect.top - lineTop) < lineHeight / 2) {
            minLeft = Math.min(minLeft, partialRect.left);
            maxRight = Math.max(maxRight, partialRect.right);
          }
        }
      }
    }
  }
  
  if (minLeft === Infinity || maxRight === -Infinity) {
    return null;
  }
  
  return { left: minLeft, right: maxRight };
}

export function getRangeRects(
  range: Range,
  container: HTMLElement | null,
  isTextBound = false,
): SelectionRect[] {
  const nativeRects = Array.from(range.getClientRects());
  const containerRect = container?.getBoundingClientRect();
  const nativeRectsMapFn = isTextBound ?
    (rect: DOMRect, index: number) => {
      const isFirstLine = index === 0;
      const isLastLine = index === nativeRects.length - 1;
      const isMiddleLine = !isFirstLine && !isLastLine && nativeRects.length > 2;
      
      // For middle lines, try to get actual text bounds
      if (isMiddleLine) {
        const textBounds = getTextBoundAtLine(range, rect.top, rect.height);
        if (textBounds) {
          return {
            width: textBounds.right - textBounds.left,
            height: rect.bottom - rect.top,
            top: rect.top - (containerRect?.top ?? 0) + (container?.scrollTop ?? 0),
            left: textBounds.left - (containerRect?.left ?? 0) + (container?.scrollLeft ?? 0),
          };
        }
      }
      
      // For first/last lines or if we couldn't determine text bounds, use original
      return {
        width: rect.right - rect.left,
        height: rect.bottom - rect.top,
        top: rect.top - (containerRect?.top ?? 0) + (container?.scrollTop ?? 0),
        left: rect.left - (containerRect?.left ?? 0) + (container?.scrollLeft ?? 0),
      };
    } :
    (rect: DOMRect) => ({
      width: rect.right - rect.left,
      height: rect.bottom - rect.top,
      top: rect.top - (containerRect?.top ?? 0) + (container?.scrollTop ?? 0),
      left:
        rect.left - (containerRect?.left ?? 0) + (container?.scrollLeft ?? 0),
    });
  
  const rectsWithoutFiltered = nativeRects
    .map(nativeRectsMapFn)
    .filter(rect => rect.width > 0 && rect.height > 0);

  return filterCoveringRects(rectsWithoutFiltered);
}
